"use client";

import { createContext, useCallback, useContext } from "react";
import {
  formatStorefrontDate,
  formatStorefrontTime,
  type FormatStorefrontDateOptions,
  type StorefrontFormatSettings,
} from "@tripcart/shared/format";
import { toBcp47Locale } from "@tripcart/shared/i18n/locale-tag";

/** Per-render options a call site chooses (the agency settings come from context). */
export type FormatDateCallOptions = Omit<
  FormatStorefrontDateOptions,
  keyof StorefrontFormatSettings
>;

const DateTimeFormatContext = createContext<StorefrontFormatSettings>({});

/**
 * Seeds the agency's date/time settings for client date components — the mirror
 * of `CurrencyFormatProvider`. Resolved server-side (root layout) and passed as
 * a plain prop: no client fetch, negligible JS (perf bar).
 */
export function DateTimeFormatProvider({
  settings,
  children,
}: {
  settings: StorefrontFormatSettings;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <DateTimeFormatContext.Provider value={settings}>
      {children}
    </DateTimeFormatContext.Provider>
  );
}

export function useDateTimeFormatSettings(): StorefrontFormatSettings {
  return useContext(DateTimeFormatContext);
}

/**
 * Returns a `formatStorefrontDate` bound to the agency's Site Settings.
 * Pass per-call options (`weekday`, `mode: "timestamp"`, `withTime`); the
 * `dateFormat`/`timeFormat`/`timezone`/`locale` come from the agency.
 */
export function useFormatDate(): (
  value: Date | string,
  opts?: FormatDateCallOptions,
) => string {
  const settings = useDateTimeFormatSettings();
  return useCallback(
    (value, opts) => formatStorefrontDate(value, { ...settings, ...opts }),
    [settings],
  );
}

/**
 * The agency's locale as a BCP-47 tag, for the `Intl` constructors that are
 * NOT about dates — `Intl.ListFormat` (guide languages), `toLocaleString` (a
 * review count).
 *
 * Every one of those used `undefined`, i.e. the runtime default: the server's
 * locale during SSR and the browser's after hydration. That is two bugs at
 * once — the operator's configured locale ignored, and a React text mismatch
 * on any value the two runtimes render differently. `DateTile` was fixed for
 * exactly this and the surrounding callers were missed.
 */
export function useLocaleTag(): string {
  return toBcp47Locale(useDateTimeFormatSettings().locale);
}

/** Returns a `formatStorefrontTime` bound to the agency's `timeFormat`. */
export function useFormatTime(): (value: string) => string {
  const settings = useDateTimeFormatSettings();
  return useCallback(
    (value) => formatStorefrontTime(value, { timeFormat: settings.timeFormat }),
    [settings],
  );
}
